import { useCallback, useState } from "react";
import { View, type LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { cn } from "@-/com.npmjs.cn";
import arrayLength from "@-/com.npmjs.extra-array__length";
import runes from "@-/com.npmjs.runes2";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  CARD_TILE_TYPE_ICONS,
  CARD_TILE_TYPE_LABELS,
  type CardTileType,
} from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile";
import {
  type GlyphStyle,
  MorphemeGlyphTileCharacter,
  getMorphemeGlyphTileGraphemeGlyphStyle,
  getMorphemeGlyphTileMultiCharacterGlyphStyle,
  type MorphemeGlyphTileStage,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import {
  MORPHEME_TILE_DELAY_LONG_PRESS_MS,
  morphemeGlyphTileVariants,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.variants";

/** The card-type icon owns the top third of the square; the glyph the bottom two thirds. */
const GLYPH_ZONE_RATIO = 2 / 3;
/** Icon size as a fraction of the top zone's height, with a readability floor. */
const ICON_ZONE_FILL_RATIO = 0.7;
const ICON_MIN_PX = 8;

export type CardMinimalTileUiProps = {
  cardType: CardTileType;
  className?: string;
  /** The card's subject: one grapheme, or a word's characters. */
  glyph: string;
  isLoading?: boolean;
  /** Whether this exact user card is part of the app-wide selection. */
  isSelected?: boolean;
  onLongPress?: () => void;
  onPress?: () => void;
  /**
   * Tone-number pinyin of a single-grapheme subject's primary reading. Colors the glyph by tone,
   * exactly like a grapheme glyph tile. Ignored for words.
   */
  primaryPinyin?: string | null;
  /**
   * The tile's width in px when the caller knows it up front (a grid with a known width). The
   * glyph then renders at its final size on the first commit; without it the tile measures itself.
   */
  size?: number;
  /** SRS stage background, shared with the grapheme glyph tiles. Defaults to `"unadded"`. */
  stage?: MorphemeGlyphTileStage;
  testID?: string;
  /** Render the glyph in the theme foreground color, ignoring tone colors. */
  uncolored?: boolean;
};

function getGlyphStyle({
  characterCount,
  tileSize,
}: {
  characterCount: number;
  tileSize: number;
}): GlyphStyle {
  const glyphZoneHeight = Math.max(1, tileSize * GLYPH_ZONE_RATIO);
  const glyphZoneWidth = Math.max(1, tileSize);
  return characterCount > 1
    ? getMorphemeGlyphTileMultiCharacterGlyphStyle({
        availableHeight: glyphZoneHeight,
        availableWidth: glyphZoneWidth,
        characterCount,
      })
    : getMorphemeGlyphTileGraphemeGlyphStyle({ glyphZoneHeight, glyphZoneWidth });
}

/**
 * A compact study *card* tile, the same square as a grapheme glyph tile: an icon for the card type
 * (meaning, pronunciation, …) in the top third and the glyph in the bottom two thirds (smaller
 * than a glyph tile's, which fills the square). For grids of cards where the full
 * `CardDetailsTileUi` (gloss, reading, due line) is too much detail.
 */
export function CardMinimalTileUi({
  cardType,
  className,
  glyph,
  isLoading = false,
  isSelected = false,
  onLongPress,
  onPress,
  primaryPinyin = null,
  size,
  stage = "unadded",
  testID,
  uncolored = false,
}: CardMinimalTileUiProps) {
  const [measuredSize, setMeasuredSize] = useState<number | null>(null);
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setMeasuredSize((previous) => (previous === width ? previous : width));
  }, []);
  const tileSize = size ?? measuredSize;
  const characters = runes(glyph);
  const characterCount = arrayLength(characters);
  const glyphStyle = tileSize === null ? null : getGlyphStyle({ characterCount, tileSize });
  const iconSize =
    tileSize === null
      ? ICON_MIN_PX
      : Math.max(ICON_MIN_PX, Math.round(tileSize * (1 - GLYPH_ZONE_RATIO) * ICON_ZONE_FILL_RATIO));

  return (
    <Button
      accessibilityLabel={`${glyph}, ${CARD_TILE_TYPE_LABELS[cardType]} card`}
      accessibilityState={{ busy: isLoading, selected: isSelected }}
      className={cn(
        morphemeGlyphTileVariants({ stage }),
        // Matches the glyph tile's selected ring (see `MorphemeGlyphTileUi`).
        isSelected && "border-1 border-solid border-primary/40",
        className,
      )}
      delayLongPress={MORPHEME_TILE_DELAY_LONG_PRESS_MS}
      isLoading={isLoading}
      onLongPress={onLongPress}
      onPress={onPress}
      size="fill"
      testID={testID}
      variant="outline"
    >
      <View
        className="absolute inset-0 flex-col overflow-hidden"
        onLayout={size === undefined ? handleLayout : undefined}
      >
        <View className="min-h-0 flex-1 items-center justify-center">
          <Icon
            className="text-muted-foreground"
            name={CARD_TILE_TYPE_ICONS[cardType]}
            size={iconSize}
          />
        </View>
        <View className="min-h-0 flex-2 flex-row items-center justify-center">
          {characters.map((character, index) => (
            <MorphemeGlyphTileCharacter
              // No pinyin dots: the tile shows only the glyph and its card type.
              additionalPinyinList={[]}
              className={characterCount > 1 ? "h-full w-auto min-w-0 shrink" : undefined}
              glyphStyle={glyphStyle}
              grapheme={character}
              key={`${character}-${index}`}
              primaryPinyin={characterCount > 1 ? null : primaryPinyin}
              uncolored={uncolored}
            />
          ))}
        </View>
      </View>
    </Button>
  );
}
